import React, { useEffect, useState, useCallback } from "react";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Loader from "../../elements/Loader";
import { toast } from "react-toastify";
import Select from "react-select";
import ApiService from "../../services/apiService";

export default function AddPromocode() {
    const params = useParams();
    const { id } = params;
    const [loading, setLoading] = useState(false);
    const [segments, setSegments] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const navigate = useNavigate();

    const [initialValues, setInitialValues] = useState({
        name: "",
        code: "",
        description: "",
        terms: "",
        discountType: "flat",
        discount_worth: null,
        min_order_amount: null,
        max_order_amount: null,
        max_discount: null,
        start_date: "",
        end_date: "",
        usage_limit: 1,
        start_immediately: false,
        segmentId: "",
        campaignId: "",
        status: "active",
        viewOnCheckout: false,
        used_count: 0,
    });

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("Name is required"),
        code: Yup.string().required("Code is required"),
        discountType: Yup.string().required("Discount type is required"),
        discount_worth: Yup.number().required("Discount value is required"),
        min_order_amount: Yup.number().required("Minimum order amount is required"),
        start_date: Yup.date().required("Start date is required"),
        end_date: Yup.date().required("End date is required"),
        usage_limit: Yup.number().required("Usage limit is required"),
        segmentId: Yup.string().required("User segment is required"),
        campaignId: Yup.string().required("Campaign is required"),
    });

    const getPromocodeById = useCallback(async () => {
        try {
            setLoading(true);
            const response = await ApiService.get(`promotion/promocode/${id}`);
            if (response?.data) {
                setInitialValues(response.data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    const getSegment = useCallback(async () => {
        try {
            const response = await ApiService.get("promotion/segment");
            if (response?.data) {
                const options = response.data.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }));
                setSegments(options);
            }
        } catch (err) {
            console.error(err);
        }
    }, []);

    const getCampaign = useCallback(async () => {
        try {
            const response = await ApiService.get("promotion/campaigns");
            if (response?.data) {
                const options = response.data.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }));
                setCampaigns(options);
            }
        } catch (err) {
            console.error(err);
        }
    }, []);

    useEffect(() => {
        if (id) getPromocodeById();
        getSegment();
        getCampaign();
    }, [id, getPromocodeById, getSegment, getCampaign]);

    const onSubmit = async (values, { setSubmitting }) => {
        try {
            if (id) {
                await ApiService.put(`promotion/promocode/${id}`, values);
                toast.success("Promocode Updated");
            } else {
                await ApiService.post("promotion/promocode", values);
                toast.success("New Promocode Added");
            }
            navigate("/promocode", { replace: true });
        } catch (error) {
            console.error("Error saving promocode:", error);
            toast.error("Failed to save promocode");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="row">
            {loading && <Loader />}
            <div className="col-xl-8 col-lg-12 offset-md-2">
                <div className="card">
                    <div className="card-header">
                        <h4 className="card-title">{id ? "Edit" : "Add"} Promocode</h4>
                    </div>
                    <div className="card-body">
                        <div className="basic-form">
                            <Formik
                                initialValues={initialValues}
                                validationSchema={validationSchema}
                                onSubmit={onSubmit}
                                enableReinitialize={true}
                            >
                                {({ isSubmitting, setFieldValue, values }) => (
                                    <Form>
                                        <div className="row">
                                            <div className="mb-3 col-md-6">
                                                <label className="form-label">Name</label>
                                                <span className="text-red">*</span>
                                                <Field name="name" type="text" className="form-control" />
                                                <ErrorMessage name="name" component="div" className="error-message" />
                                            </div>

                                            <div className="mb-3 col-md-6">
                                                <label className="form-label">Code</label>
                                                <span className="text-red">*</span>
                                                <Field name="code" type="text" className="form-control" />
                                                <ErrorMessage name="code" component="div" className="error-message" />
                                            </div>
                                            <div className="mb-3 col-md-6">
                                                <label className="form-label">Select User Segment</label>
                                                <span className="text-red">*</span>
                                                <Select
                                                    name="segmentId"
                                                    options={segments}
                                                    onChange={(selectedOptions) =>
                                                    setFieldValue("segmentId", selectedOptions?.value)
                                                    }
                                                    value={segments?.find((item) => item.value === values?.segmentId)}

                                                />
                                                <ErrorMessage name="segmentId" component="div" className="error-message" />
                                            </div>

                                            <div className="mb-3 col-md-6">
                                                <label className="form-label">Select Campaign</label>
                                                <span className="text-red">*</span>
                                                <Select
                                                    name="campaignId"
                                                    options={campaigns}
                                                    onChange={(selectedOptions) =>
                                                    setFieldValue("campaignId", selectedOptions?.value)
                                                    }
                                                    value={campaigns?.find((item) => item.value === values?.campaignId)}

                                                />
                                                <ErrorMessage name="campaignId" component="div" className="error-message" />
                                            </div>

                                            <div className="mb-3 col-md-12">
                                                <label className="form-label">Description</label>
                                                <Field as="textarea" name="description" className="form-control" />
                                            </div>

                                            <div className="mb-3 col-md-12">
                                                <label className="form-label">Terms</label>
                                                <Field as="textarea" name="terms" className="form-control" />
                                            </div>

                                            <div className="mb-3 col-md-6">
                                                <label className="form-label">Discount Type</label>
                                                <span className="text-red">*</span>
                                                <Field as="select" name="discountType" className="form-control">
                                                    <option value="flat">Flat</option>
                                                    <option value="percentage">Percentage</option>
                                                </Field>
                                                <ErrorMessage name="discountType" component="div" className="error-message" />
                                            </div>

                                            <div className="mb-3 col-md-6">
                                                <label className="form-label">Discount Value {values.discountType === 'percentage' ? <span className="">% </span>: '' }</label>
                                                <span className="text-red">*</span>
                                                <Field name="discount_worth" type="number" className="form-control" />
                                                
                                                <ErrorMessage name="discount_worth" component="div" className="error-message" />
                                            </div>

                                            <div className="mb-3 col-md-6">
                                                <label className="form-label">Min Order Amount</label>
                                                <span className="text-red">*</span>
                                                <Field name="min_order_amount" type="number" className="form-control" />
                                                <ErrorMessage name="min_order_amount" component="div" className="error-message" />
                                            </div>

                                            <div className="mb-3 col-md-6">
                                                <label className="form-label">Max Order Amount</label>
                                                <Field name="max_order_amount" type="number" className="form-control" />
                                            </div>

                                    

                                            <div className="mb-3 col-md-6">
                                                <label className="form-label">Start Date</label>
                                                <span className="text-red">*</span>
                                                <Field name="start_date" type="date" className="form-control" />
                                                <ErrorMessage name="start_date" component="div" className="error-message" />
                                            </div>

                                            <div className="mb-3 col-md-6">
                                                <label className="form-label">End Date</label>
                                                <span className="text-red">*</span>
                                                <Field name="end_date" type="date" className="form-control" />
                                                <ErrorMessage name="end_date" component="div" className="error-message" />
                                            </div>

                                            <div className="mb-3 col-md-6">
                                                <label className="form-label">Max Discount</label>
                                                <Field name="max_discount" type="number" className="form-control" />
                                            </div>

                                            <div className="mb-3 col-md-6">
                                                <label className="form-label">Usage Limit</label>
                                               
                                                <Field name="usage_limit" type="number" className="form-control" />
                                                <ErrorMessage name="usage_limit" component="div" className="error-message" />
                                                <span className="text-muted">The total number of times the promo code can be redeemed across all users.</span>
                                                <span className="text-bold"> ({values.used_count}/{values.usage_limit} times used.
                                                {values.usage_limit > 0 
                                                        ? ` ${values.usage_limit - values.used_count} redemptions remaining.`
                                                        : ' Unlimited usage.'
                                                    })
                                                </span>
                                            </div>

                                            <div className="mb-3 col-md-6">
                                                <Field name="start_immediately" type="checkbox" className="form-check-input" />
                                                <label className="form-label center ml-2 mt-1">Start Immediately</label>
                                            </div>

                                            <div className="mb-3 col-md-6">
                                                <Field name="viewOnCheckout" type="checkbox" className="form-check-input" />
                                                <label className="form-label center ml-2 mt-1">Visible on Checkout</label>
                                            </div>

                                            <div className="mb-3 col-md-6">
                                                <label className="form-label">Status</label>
                                                <Field as="select" name="status" className="form-control">
                                                    <option 
                                                        value="active" 
                                                        disabled={values.usage_limit > 0 && values.used_count >= values.usage_limit}
                                                    >
                                                        Active
                                                    </option>
                                                    <option value="inactive">Inactive</option>
                                                    <option value="expired">Expired</option>
                                                </Field>
                                            </div>
                                        </div>

                                        <button type="submit" disabled={isSubmitting} className="btn btn-primary">
                                            Save
                                        </button>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}